import { useEffect, useState } from "react";

import { ACTIVE_BOOKING_URL } from "@/config/bookingLinks.js";
const TEAM_IMG =
  "/images/team/team-hero.png?v=2";

export default function Hero() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(t);
  }, []);

  const anim = (delay: string): React.CSSProperties => ({
    transition:
      "opacity 750ms cubic-bezier(0.22,1,0.36,1), transform 750ms cubic-bezier(0.22,1,0.36,1)",
    transitionDelay: delay,
    ...(loaded
      ? { opacity: 1, transform: "translateY(0)" }
      : { opacity: 0, transform: "translateY(24px)" }),
  });

  return (
    <section className="hero-fadein w-full">
      {/* ── DESKTOP md+ : full-width image, dark cinematic overlay, text at bottom ── */}
      <div className="relative hidden min-h-[500px] w-full aspect-[1672/941] overflow-hidden md:block lg:min-h-[560px]">
        <img
          src={TEAM_IMG}
          alt="Perspective Therapy team"
          className="absolute inset-0 block h-full w-full object-cover"
        />

        {/* Bottom dark gradient for text legibility */}
        <div
          className="absolute inset-x-0 bottom-0 pointer-events-none"
          style={{
            height: "52%",
            background:
              "linear-gradient(to top, rgba(14,10,6,0.90) 0%, rgba(14,10,6,0.50) 42%, transparent 100%)",
          }}
        />

        {/* Soft vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 90% 70% at 50% 40%, transparent 48%, rgba(14,10,6,0.18) 100%)",
          }}
        />

        {/* Text pinned to bottom-left */}
        <div className="absolute inset-x-0 bottom-0 z-10 px-12 lg:px-20 pb-12">
          <div className="max-w-2xl">
            <h1
              style={anim("180ms")}
              className="font-fraunces text-white text-4xl lg:text-[2.7rem] font-light leading-[1.18] mb-4 tracking-tight"
            >
              Therapy that helps you understand what&apos;s actually going on{" "}
              <em className="not-italic font-normal" style={{ color: "#C9BCEE" }}>
                and change what isn&apos;t working.
              </em>
            </h1>

            <p
              style={anim("340ms")}
              className="font-manrope text-white/80 text-base lg:text-lg font-light leading-relaxed mb-8 max-w-xl"
            >
              Thoughtful therapy for children, teens, adults, couples, and groups. Meet in person in Larchmont, New York, or connect by telehealth in NY, NJ, or IN, depending on therapist eligibility.
            </p>

            <div style={anim("480ms")}>
              <a
                href={ACTIVE_BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2.5 text-white font-manrope font-semibold text-base px-9 py-4 rounded-full transition-all cursor-pointer whitespace-nowrap"
                style={{ background: "#3C6FF8" }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.background = "#2B5BE0";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.background = "#3C6FF8";
                }}
              >
                Book Now
                <i className="ri-arrow-right-line text-base" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── MOBILE < md : faces fully clear navbar, warm strip behind it ── */}
      <div className="md:hidden w-full" style={{ background: "#C6B5A8" }}>
        {/* 92px spacer — clears announcement bar (34px) + main nav (58px); warm photo tone shows behind navbar */}
        <div style={{ height: "92px" }} />

        {/* Image starts fully below navbar — all faces visible */}
        <div className="relative w-full aspect-[1672/941] min-h-[210px]">
          <img
            src={TEAM_IMG}
            alt="Perspective Therapy team"
            className="absolute inset-0 block h-full w-full object-cover"
          />

          {/* Very short cream fade at base — bridges image into text section */}
          <div
            className="absolute inset-x-0 bottom-0 pointer-events-none"
            style={{
              height: "18%",
              background: "linear-gradient(to top, #FBF9F6 0%, transparent 100%)",
            }}
          />
        </div>

        {/* Text — sits below the image on brand cream */}
        <div className="px-5 pt-4 pb-10" style={{ background: "#FBF9F6" }}>
          <h1
            style={anim("180ms")}
            className="font-fraunces text-[#2B2B2B] text-[1.65rem] font-light leading-[1.22] mb-3 tracking-tight"
          >
            Therapy that helps you understand what&apos;s actually going on{" "}
            <em className="not-italic font-normal" style={{ color: "#7D54FC" }}>
              and change what isn&apos;t working.
            </em>
          </h1>

          <p
            style={anim("320ms")}
            className="font-manrope text-[#2B2B2B]/65 text-sm font-light leading-relaxed mb-7"
          >
            Therapy for self-aware teens, adults, and couples who are ready to
            stop repeating the same emotional patterns and start building a more
            grounded, connected life.
          </p>

          <div style={anim("460ms")}>
            <a
              href={ACTIVE_BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2.5 text-white font-manrope font-semibold text-sm px-7 py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap"
              style={{ background: "#3C6FF8" }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = "#2B5BE0";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = "#3C6FF8";
              }}
            >
              Book Now
              <i className="ri-arrow-right-line text-sm" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
